import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import axios from 'axios';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { environment } from 'src/environments/environment';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PulseSyncDirective } from '../pulse-sync.directive';
import i18n from './translate';
import { CaseTableItem } from '../types';
import {
  NotificationPreviewDialogComponent,
  NotificationPreviewDialogData,
} from '../notification-preview-dialog/notification-preview-dialog.component';

type EventFile = {
  id: string;
  file_name: string;
};

type Payment = {
  amount: number;
  original_amount: number | null;
  label: string | null;
  currency_iso: string;
};

type Notification = {
  routes: string[];
  subject: string;
  media_uuid?: string | null;
  attachments: {
    id: string;
    file_name: string;
  }[];
};

type StateChip = {
  label: string;
  text_color: string;
  background_color: string;
  ongoing: boolean;
};

type EventData = {
  happenedAt: string;
  label: string;
  params: any;
  files: EventFile[];
  payments: Payment[];
  notifications: Notification[];
  status: 'failed' | 'success' | 'pending';
  transitionLogId?: number | null;
  fromState?: StateChip | null;
  toState?: StateChip | null;
};

export type CaseEventsMokkContext = {
  id: number;
  transition_logs: {
    id: number;
    result: string;
    label: string;
    created_at: string | null;
    files: {
      uuid: string;
      file_name: string;
      mime_type: string;
      url: string;
    }[];
  }[];
};

@Component({
  selector: 'app-case-events',
  templateUrl: './case-events.component.html',
  styleUrls: ['./case-events.component.scss'],
  imports: [
    MatIconButton,
    MatTooltip,
    MatIcon,
    PayeeIconComponent,
    DecimalPipe,
    DatePipe,
    TranslateModule,
    PulseSyncDirective,
  ],
})
export class CaseEventsComponent {
  private snackbar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private workflowService = inject(WorkflowService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  private static translationsLoaded = false;

  readonly caseItem = input.required<CaseTableItem>();
  readonly mokk = input<CaseEventsMokkContext | null>(null);

  private readonly caseLogs = signal<EventData[]>([]);
  readonly shownLogs = signal<EventData[]>([]);
  readonly pulsingLog = signal<EventData | null>(null);
  readonly isLoading = signal(true);
  readonly showAllLogs = signal(false);
  readonly mokkLinkBusy = signal(false);

  readonly hasMoreThanFiveEvents = computed(() => this.caseLogs().length > 5);

  constructor() {
    if (!CaseEventsComponent.translationsLoaded) {
      for (const [lang, translation] of Object.entries(i18n)) {
        this.translate.setTranslation(lang, translation, true);
      }
      CaseEventsComponent.translationsLoaded = true;
    }

    effect(() => {
      void this.loadEvents();
    });

    this.workflowService.afterWorkflow
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.loadEvents();
      });
  }

  downloadUrlOfFile(file: EventFile): string {
    return this.downloadUrlOfMediaUuid(file.id);
  }

  downloadUrlOfMediaUuid(mediaUuid: string): string {
    return `${environment.baseUrl}/api/file/${mediaUuid}`;
  }

  openNotificationPreview(
    notification: Notification,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    this.dialog.open<
      NotificationPreviewDialogComponent,
      NotificationPreviewDialogData
    >(NotificationPreviewDialogComponent, {
      maxWidth: '95vw',
      width: 'min(95vw, 1280px)',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: ['payee-dialog', 'notification-preview-dialog'],
      data: {
        subject: notification.subject,
        mediaUuid: notification.media_uuid ?? null,
        attachments: notification.attachments,
        routes: notification.routes ?? [],
      },
    });
  }

  getEventLabelMediaUuid(caseLog: EventData): string | null {
    if (caseLog.notifications.length !== 1) {
      return null;
    }

    return caseLog.notifications[0]?.media_uuid ?? null;
  }

  shouldLinkEventLabel(caseLog: EventData): boolean {
    return !!this.getEventLabelMediaUuid(caseLog);
  }

  shouldShowNotificationLinks(caseLog: EventData): boolean {
    return caseLog.notifications.length > 1;
  }

  getRoutesTooltip(routes: string[]): string | null {
    const uniqueRoutes = [
      ...new Set(routes.map((route) => route.trim()).filter(Boolean)),
    ];

    if (uniqueRoutes.length === 0) {
      return null;
    }

    return uniqueRoutes.map((route) => `• ${route}`).join('\n');
  }

  getEventLabelNotificationRoutesTooltip(caseLog: EventData): string | null {
    if (caseLog.notifications.length !== 1) {
      return null;
    }

    return this.getRoutesTooltip(caseLog.notifications[0]?.routes ?? []);
  }

  getExternalIdTypeLabel(type: string | null | undefined): string {
    if (!type) {
      return '';
    }

    const typeKey = `CASE_EVENTS.EXTERNAL_ID_TYPE.${type.toUpperCase()}`;
    const translated = this.translate.instant(typeKey);

    if (translated === typeKey) {
      return type;
    }

    return translated;
  }

  isMokkAttachedToLog(caseLog: EventData): boolean {
    const logId = caseLog.transitionLogId;
    const mokk = this.mokk();
    if (!mokk || !logId) {
      return false;
    }
    return mokk.transition_logs.some((l) => l.id === logId);
  }

  async attachMokkToLog(caseLog: EventData, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    const logId = caseLog.transitionLogId;
    const mokk = this.mokk();
    if (mokk == null || logId == null || this.mokkLinkBusy()) {
      return;
    }

    this.mokkLinkBusy.set(true);
    try {
      await axios.post(
        `${environment.baseUrl}/api/payee-case-mokk/${mokk.id}/transition-logs/${logId}`,
      );
      if (!this.isMokkAttachedToLog(caseLog)) {
        mokk.transition_logs.push({
          id: logId,
          created_at: caseLog.happenedAt,
          files: [],
          label: caseLog.label,
          result: caseLog.status,
        });
      }
    } catch (e) {
      console.error('Error while attaching MOKK to transition log', e);
      this.snackbar.open(
        this.translate.instant(
          'CASE_EVENTS.ERROR_MESSAGES.ATTACH_MOKK_TO_LOG_FAILED',
        ),
        this.translate.instant('CASE_EVENTS.OK'),
        { duration: 5000 },
      );
    } finally {
      this.mokkLinkBusy.set(false);
    }
  }

  async detachMokkFromLog(
    caseLog: EventData,
    event: MouseEvent,
  ): Promise<void> {
    event.stopPropagation();
    const logId = caseLog.transitionLogId;
    const mokk = this.mokk();
    if (mokk == null || logId == null || this.mokkLinkBusy()) {
      return;
    }

    this.mokkLinkBusy.set(true);
    try {
      await axios.delete(
        `${environment.baseUrl}/api/payee-case-mokk/${mokk.id}/transition-logs/${logId}`,
      );
      mokk.transition_logs = mokk.transition_logs.filter((l) => l.id !== logId);
    } catch (e) {
      console.error('Error while detaching MOKK from transition log', e);
      this.snackbar.open(
        this.translate.instant(
          'CASE_EVENTS.ERROR_MESSAGES.DETACH_MOKK_FROM_LOG_FAILED',
        ),
        this.translate.instant('CASE_EVENTS.OK'),
        { duration: 5000 },
      );
    } finally {
      this.mokkLinkBusy.set(false);
    }
  }

  toggleShowAllLogs(showAllLogs?: boolean): void {
    const nextShowAll = showAllLogs ?? !this.showAllLogs();
    this.showAllLogs.set(nextShowAll);

    const logs = this.caseLogs();
    if (nextShowAll) {
      this.shownLogs.set(logs);
      return;
    }

    const indexOfFirstSuccess = logs.findIndex((l) => l.status === 'success');
    this.shownLogs.set(logs.slice(0, Math.max(5, indexOfFirstSuccess + 1)));
  }

  private async loadEvents(): Promise<void> {
    const caseId = this.caseItem().id;

    this.isLoading.set(true);
    this.caseLogs.set([]);
    this.shownLogs.set([]);
    this.pulsingLog.set(null);

    try {
      const url = environment.baseUrl + `/api/case-events/${caseId}`;
      const result = await axios.get<{ events: EventData[]; }>(url);

      if (caseId !== this.caseItem().id) {
        return;
      }

      const events = result.data.events;
      const current = events.find((entry) => entry.toState?.label === this.caseItem().state.state_label);
      this.caseLogs.set(events);
      this.pulsingLog.set(current?.toState?.ongoing ? current : null);
      this.toggleShowAllLogs(false);
    } catch (error) {
      if (caseId !== this.caseItem().id) {
        return;
      }

      this.snackbar.open(
        this.translate.instant(
          'CASE_EVENTS.ERROR_MESSAGES.UNKNOWN_ERROR_WHILE_LOADING_EVENTS',
        ),
        this.translate.instant('CASE_EVENTS.OK'),
        {
          duration: 5000,
        },
      );
      console.error('Error while loading events', error);
    } finally {
      if (caseId !== this.caseItem().id) {
        this.isLoading.set(false);
      }
    }
  }
}
